import React, { useState, useEffect, useMemo, useCallback } from "react";

import { OutlinedInput } from "@material-ui/core";

import { ImageUploader, ErrorText } from "../../../widgets";
import makeStyles from "@material-ui/core/styles/makeStyles";
import style from "../style.module.css";
import greyImagePlaceholder from "assets/images/placeholder-image.png";
import {
  template_field_keys,
  FORM_FIELDS,
  FONT_LIST_DEFAULTS,
  INFINITE_SCROLL_OFFSET,
} from "ui/pages/TemplatePreview/constants";
import { IMAGE_SECTION_KEY_PATH } from "features/Common/modules/ExlyImage/constants/ExlyImage.constants";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";

import DeleteIcon from "assets/images/delete_lined.svg";
import ExlyImage from "common/Components/ExlyImage";
import ExlyAutoComplete from "common/form/ExlyAutoComplete";

import { LOGO_TEXT_MAX_LENGTH } from "ui/pages/TemplatePreview/constants.js";
import useTemplatePreview from "ui/pages/TemplatePreview/hooks/useTemplatePreview";
import debounce from "lodash/debounce";

const useStyles = (isMobile) =>
  makeStyles(() => ({
    dropdownList: {
      width: "fit-content",
      maxWidth: isMobile ? "80vw" : "40vw",
      "& *": {
        fontFamily: "unset",
      },
    },
    dropdownItem: {
      fontSize: 16,
    },
  }));

const loadFontUrl = (url, callback) => {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  link.onload = callback;
  document.getElementsByTagName("head")[0].appendChild(link);
};

export const UploadLogo = (props) => {
  const {
    state,
    setField,
    errorState,
    setErrorMessage,
    enableError,
    isMobile,
    fontList = [],
    isFontListLoading,
    handleFontSearchChange,
    disableAllEdits,
    loadMoreFonts,
    totalFonts,
  } = props;

  const { validateText } = useTemplatePreview();

  const { social_info, hero_section_title, favicon, logo_image } =
    template_field_keys;

  const muiStyle = useStyles(isMobile)();

  const [fontLoaded, setFontLoaded] = useState({});

  const handleScroll = useCallback(
    (event) => {
      if (!loadMoreFonts || isFontListLoading) return;

      const listboxNode = event.currentTarget;
      const scrollTop = listboxNode.scrollTop;
      const scrollHeight = listboxNode.scrollHeight;
      const clientHeight = listboxNode.clientHeight;

      // Load more when within 100px of bottom
      const isNearBottom =
        scrollHeight - scrollTop <= clientHeight + INFINITE_SCROLL_OFFSET;

      if (isNearBottom) {
        loadMoreFonts();
      }
    },
    [loadMoreFonts, isFontListLoading]
  );

  useEffect(() => {
    fontList.forEach(({ url, slug }) => {
      if (!fontLoaded?.[slug]) {
        loadFontUrl(url, () => {
          setFontLoaded((fontLoadedProp) => ({
            ...fontLoadedProp,
            [slug]: true,
          }));
        });
      }
    });
  }, [fontList]);

  const handleSearchChange = (value) => {
    if (!handleFontSearchChange) return;

    debounceSearch(value);
  };

  const debounceSearch = useMemo(
    () =>
      debounce((value) => {
        handleFontSearchChange(value);
      }, 300),
    [handleFontSearchChange]
  );

  const fontOptions = fontList.map(({ title, slug }) => ({
    id: slug,
    title,
    label: (
      <div
        key={slug}
        style={{ fontFamily: title }}
        className={muiStyle.dropdownItem}
      >
        {title}
        {fontLoaded[slug] ? "" : "(Loading Preview...)"}
      </div>
    ),
    value: slug,
  }));

  const selectedFontOption = useMemo(() => {
    if (!state?.font || checkIsArrayEmpty(fontOptions)) return null;

    return (
      fontOptions.find((option) => option.value === state.font.slug) || null
    );
  }, [state?.font, fontOptions]);

  const handleBlur = (fieldName) => {
    enableError(fieldName);

    const value = state?.social_info?.[fieldName];

    if (value) {
      setField(["social_info", fieldName], value.trim());
    }
  };

  return (
    <>
      <div className="tw-py-1">
        <div className="tw-py-1">
          <div className="tw-font-medium tw-text-[#272522] tw-tracking-[0.2px] tw-text-sm tw-mt-1">
            Logo Image{" "}
            <span className="tw-text-[#272522] tw-opacity-60">
              (will be shown by default)
            </span>
          </div>

          <div className="tw-my-[6px] tw-relative">
            <ImageUploader
              uploadUrl={state?.social_info?.logo_image}
              setUploadUrl={(firebaseUrl) =>
                setField([social_info.name, logo_image.name], firebaseUrl)
              }
              className={style.logoImageUploader}
              hoverText="Upload Image"
              customDefaultImage={greyImagePlaceholder}
              fullWidth={250}
              width={250}
              height={60}
              borderRadius="2%"
              query={"logo"}
              id="website_logo_appearance"
              disabled={disableAllEdits}
              uploadViaOptimization
              sectionName={IMAGE_SECTION_KEY_PATH.HOME_PAGE}
            />
          </div>
          {state?.social_info?.logo_image ? (
            <div
              className="tw-text-[#CD1C1C] tw-flex tw-items-center -tw-mt-2 tw-mb-2 tw-gap-1 tw-cursor-pointer"
              onClick={() => {
                setField([social_info.name, logo_image.name], null);
              }}
            >
              <ExlyImage
                src={DeleteIcon}
                height={12}
                width={12}
                alt="delete"
                className="tw-cursor-pointer"
              />
              <span style={{ fontSize: "12px", color: "#CD1C1C" }}>
                Remove Logo
              </span>
            </div>
          ) : null}
        </div>

        <div className="tw-font-medium tw-text-[#272522] tw-tracking-[0.2px] tw-text-sm tw-mt-1">
          Logo text{" "}
          <span className="tw-text-[#272522] tw-opacity-60">
            (will be shown if no logo image added)
          </span>
        </div>
        <div className="tw-my-[6px] tw-relative">
          <OutlinedInput
            className={style.input}
            disabled={disableAllEdits}
            placeholder="Enter logo text"
            value={state?.social_info?.logo_text}
            name={FORM_FIELDS.logo_text}
            inputProps={{ maxLength: LOGO_TEXT_MAX_LENGTH }}
            onChange={(e) => {
              const inputVal = e?.target?.value;
              setField(
                [FORM_FIELDS.social_info, FORM_FIELDS.logo_text],
                inputVal || ""
              );

              validateText({
                value: inputVal,
                enableError,
                setErrorMessage,
                enableErrorText: FORM_FIELDS.logo_text,
                maxLength: LOGO_TEXT_MAX_LENGTH,
                errorMessage: (max) =>
                  `Maximum field length of ${max} reached.`,
              });
            }}
            onBlur={() => handleBlur(FORM_FIELDS.logo_text)}
          />
          <ErrorText errorState={errorState} fieldKey={FORM_FIELDS.logo_text} />
        </div>
      </div>

      <hr />

      <div className="tw-py-1">
        <div className="tw-font-medium tw-text-[#272522] tw-tracking-[0.2px] tw-text-sm tw-mt-1">
          Tab Title{" "}
          <span className="tw-text-[#272522] tw-opacity-60">
            (will be shown in browser&apos;s tab)
          </span>
        </div>
        <div className="tw-my-[6px] tw-relative">
          <OutlinedInput
            className={style.input}
            value={state?.social_info?.hero_section_title}
            name="title"
            disabled={disableAllEdits}
            inputProps={{ maxLength: LOGO_TEXT_MAX_LENGTH }}
            onChange={(e) => {
              const inputVal = `${e.target.value || ""}`;
              setField([social_info.name, hero_section_title.name], inputVal);

              validateText({
                value: inputVal,
                enableError,
                setErrorMessage,
                enableErrorText: hero_section_title.name,
                maxLength: LOGO_TEXT_MAX_LENGTH,
                errorMessage: (max) =>
                  `Maximum field length of ${max} reached.`,
              });
            }}
            onBlur={() => {
              enableError(hero_section_title.name);
              if (state?.social_info?.hero_section_title) {
                setField(
                  [social_info.name, hero_section_title.name],
                  state?.social_info?.hero_section_title?.trim()
                );
              }
            }}
          />
          <ErrorText
            errorState={errorState}
            fieldKey={hero_section_title.name}
          />
        </div>
      </div>
      <div className="tw-py-1">
        <div className="tw-font-medium tw-text-[#272522] tw-tracking-[0.2px] tw-text-sm tw-mt-1">
          Tab Icon{" "}
          <span className="tw-text-[#272522] tw-opacity-60">
            (will be shown in browser&apos;s tab)
          </span>
        </div>
        <div className="tw-my-[6px] tw-relative">
          <ImageUploader
            uploadUrl={state?.social_info?.favicon}
            setUploadUrl={(firebaseUrl) =>
              setField([social_info.name, favicon.name], firebaseUrl)
            }
            customDefaultImage={state?.display_image || greyImagePlaceholder}
            hoverText="Upload Image"
            fullWidth={100}
            width={100}
            height={100}
            borderRadius="2%"
            query={"icon"}
            id="website_icon_appearance"
            disabled={disableAllEdits}
            uploadViaOptimization
            sectionName={IMAGE_SECTION_KEY_PATH.HOME_PAGE}
          />
        </div>
        {state?.social_info?.favicon ? (
          <div
            className="tw-text-[#CD1C1C] tw-flex tw-items-center -tw-mt-2 tw-mb-2 tw-gap-1 tw-cursor-pointer"
            onClick={() => {
              setField([social_info.name, favicon.name], null);
            }}
          >
            <ExlyImage
              src={DeleteIcon}
              height={12}
              width={12}
              alt="delete"
              className="tw-cursor-pointer"
            />
            <span style={{ fontSize: "12px", color: "#CD1C1C" }}>
              Remove Tab Icon
            </span>
          </div>
        ) : null}
      </div>

      <hr />

      <div className="tw-py-1">
        <div className="tw-font-medium tw-text-[#272522] tw-tracking-[0.2px] tw-text-sm tw-mb-[7px]">
          Font Family{" "}
          <span className="tw-text-[#272522] tw-opacity-60">
            (Brand font applied across your site)
          </span>
        </div>

        <ExlyAutoComplete
          input={{
            value: selectedFontOption,
            onChange: () => {},
          }}
          meta={{}}
          showApplyCta
          disabled={disableAllEdits}
          options={fontOptions || []}
          loading={isFontListLoading}
          limit={totalFonts || FONT_LIST_DEFAULTS.DEFAULT_PAGE_SIZE}
          onChange={(fontOption) => {
            if (!fontOption?.value) {
              setField(FORM_FIELDS.font, null);
              return;
            }

            const selectedFont = fontList?.find(
              (f) => f?.slug === fontOption.value
            );

            setField(FORM_FIELDS.font, selectedFont || null);
          }}
          getOptionLabel={(option) => option?.title || ""}
          renderOption={(option) => {
            if (!option) return null;

            return (
              <div
                style={{ fontFamily: option?.title }}
                className={muiStyle.dropdownItem}
              >
                {option?.title}
                {!fontLoaded?.[option?.value] && " (Loading Preview...)"}
              </div>
            );
          }}
          inputProps={{
            placeholder: "Search fonts",
            onChange: ({ target }) => {
              handleSearchChange(target?.value || "");
            },
          }}
          ListboxProps={{
            onScroll: handleScroll,
          }}
        />
      </div>
    </>
  );
};
